import React, { useEffect, useRef, useState } from "react";
import { fetchHighResUrl, peekCachedHighResUrl, thumbnailUrl } from "../utils/galleryImageUrls";
import { galleryImageAlt } from "../utils/publicGalleryStrings";
import { GalleryImageTile, GALLERY_GRID_PRIORITY_COUNT } from "./GalleryImageTile";

function imageKey(img, idx) {
  return img.id ?? `idx-${idx}`;
}

function preloadImage(url) {
  if (!url) return;
  const img = new Image();
  img.decoding = "async";
  img.src = url;
}

/**
 * Grid + lightbox shared by CollectionDetail and LocationDetailPage — same tile layout,
 * keyboard nav, and modal, differing only in where `items` come from and the surrounding
 * page header/breadcrumb (left to the caller).
 */
export function GalleryDetailGrid({
  items,
  loading,
  error,
  altContext,
  emptyMessage = "No images found.",
}) {
  const [modalIdx, setModalIdx] = useState(null);
  // Modal opens on the low-res grid thumbnail (already loaded) and upgrades to the true
  // high-res original once it's signed and fetched — same pattern as Portfolio.js.
  const [modalHighResUrl, setModalHighResUrl] = useState(null);
  const [imageAspects, setImageAspects] = useState({});
  const imgRefs = useRef({});

  // CollectionDetail/LocationDetailPage don't remount this component between datasets (only the
  // route param changes), so a lightbox left open from the previous items array would otherwise
  // point at the wrong photo (or a now out-of-range index) once the new data arrives.
  useEffect(() => {
    setModalIdx(null);
    setImageAspects({});
  }, [items]);

  const getGridClass = (img, idx) => {
    const k = imageKey(img, idx);
    if (img.width && img.height) {
      const ratio = img.width / img.height;
      if (ratio > 2) return "grid-panorama";
      if (ratio < 1) return "grid-vertical";
      return "grid-1x1";
    }
    if (imageAspects[k]) {
      const ratio = imageAspects[k];
      if (ratio > 2) return "grid-panorama";
      if (ratio < 1) return "grid-vertical";
      return "grid-1x1";
    }
    return "grid-1x1";
  };

  const handleImageLoad = (img, idx) => {
    const k = imageKey(img, idx);
    const imgEl = imgRefs.current[k];
    if (imgEl?.naturalWidth && imgEl.naturalHeight) {
      const ratio = imgEl.naturalWidth / imgEl.naturalHeight;
      setImageAspects((prev) => ({ ...prev, [k]: ratio }));
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (modalIdx === null) return;
      if (e.key === "Escape") setModalIdx(null);
      if (e.key === "ArrowRight")
        setModalIdx((idx) => (idx + 1) % items.length);
      if (e.key === "ArrowLeft")
        setModalIdx((idx) => (idx - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalIdx, items.length]);

  useEffect(() => {
    if (modalIdx === null || items.length === 0) return undefined;
    let alive = true;
    // Skip the upgrading-blur flash if this image's high-res URL is already warm in cache
    // (e.g. it was preloaded as a neighbor, or the visitor is reopening a recently viewed photo).
    setModalHighResUrl(peekCachedHighResUrl(items[modalIdx]));
    fetchHighResUrl(items[modalIdx]).then((url) => {
      if (alive) setModalHighResUrl(url);
    });

    const prevIdx = (modalIdx - 1 + items.length) % items.length;
    const nextIdx = (modalIdx + 1) % items.length;
    fetchHighResUrl(items[prevIdx]).then(preloadImage);
    fetchHighResUrl(items[nextIdx]).then(preloadImage);

    return () => {
      alive = false;
    };
  }, [modalIdx, items]);

  if (loading) return <p>Loading images...</p>;
  if (error) return <p className="error-message">Error: {error}</p>;

  return (
    <>
      {items.length > 0 ? (
        <div className="portfolio-grid collection-detail-grid">
          {items.map((image, idx) => {
            const k = imageKey(image, idx);
            return (
              <GalleryImageTile
                key={k}
                idx={idx}
                className={`portfolio-card ${getGridClass(image, idx)}`}
                onClick={() => setModalIdx(idx)}
                childrenWhenVisible={() => (
                  <>
                    <img
                      ref={(el) => {
                        imgRefs.current[k] = el;
                      }}
                      src={thumbnailUrl(image)}
                      alt={galleryImageAlt(image, altContext)}
                      loading={idx < GALLERY_GRID_PRIORITY_COUNT ? "eager" : "lazy"}
                      decoding="async"
                      fetchPriority={idx < GALLERY_GRID_PRIORITY_COUNT ? "high" : "low"}
                      draggable={false}
                      onLoad={() => handleImageLoad(image, idx)}
                      onError={(e) => {
                        console.error(`Failed to load image: ${thumbnailUrl(image)}`);
                        e.target.src = "https://placehold.co/600x400?text=Image+Not+Found";
                      }}
                    />
                    {(image.title || image.description) && (
                      <div className="card-overlay">
                        {image.title && <span className="card-title">{image.title}</span>}
                        {image.description && (
                          <p className="card-description">{image.description}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              />
            );
          })}
        </div>
      ) : (
        <p>{emptyMessage}</p>
      )}

      {modalIdx !== null && items[modalIdx] && (
        <div className="portfolio-modal" onClick={() => setModalIdx(null)}>
          <button
            type="button"
            className="portfolio-modal-nav portfolio-modal-prev"
            onClick={(e) => {
              e.stopPropagation();
              setModalIdx((modalIdx - 1 + items.length) % items.length);
            }}
            aria-label="Previous"
          >
            &#8592;
          </button>

          <div className="portfolio-modal-content">
            <div className="portfolio-modal-stage">
              <img
                src={modalHighResUrl || thumbnailUrl(items[modalIdx])}
                alt={galleryImageAlt(items[modalIdx], altContext)}
                title="Click for next image"
                className={`portfolio-modal-img ${modalHighResUrl ? "" : "is-upgrading"}`}
                fetchPriority="high"
                decoding="async"
                onClick={(e) => {
                  e.stopPropagation();
                  setModalIdx((i) => {
                    if (items.length <= 1) return null;
                    return (i + 1) % items.length;
                  });
                }}
                onError={(e) => {
                  console.error(`Failed to load modal image: ${modalHighResUrl}`);
                  e.target.src = "https://placehold.co/800x600?text=Image+Not+Available";
                }}
              />
            </div>
            {(items[modalIdx].title || items[modalIdx].description) && (
              <div className="portfolio-modal-info">
                {items[modalIdx].title && (
                  <h3 className="portfolio-modal-title">{items[modalIdx].title}</h3>
                )}
                {items[modalIdx].description && (
                  <p className="portfolio-modal-description">{items[modalIdx].description}</p>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="portfolio-modal-nav portfolio-modal-next"
            onClick={(e) => {
              e.stopPropagation();
              setModalIdx((modalIdx + 1) % items.length);
            }}
            aria-label="Next"
          >
            &#8594;
          </button>
        </div>
      )}
    </>
  );
}
